const patchedFlag = Symbol.for("leviatano.tolerateExternalDomChanges");

type PatchableNode = Node & { [patchedFlag]?: boolean };

/**
 * React 17 inserts and removes with the parent it rendered.
 * A browser translation pass, or any other agent, can move that child first.
 * The native call then throws NotFoundError and the root error boundary
 * renders the Ops screen. Place the node next to the child where it lives now.
 */
export default function tolerateExternalDomChanges(): void {
  if (typeof window === "undefined" || typeof Node !== "function") {
    return;
  }

  const prototype = Node.prototype as PatchableNode;
  if (prototype[patchedFlag]) {
    return;
  }
  prototype[patchedFlag] = true;

  const nativeRemoveChild = prototype.removeChild;
  prototype.removeChild = function removeChild<T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      return child;
    }
    return nativeRemoveChild.call(this, child) as T;
  };

  const nativeInsertBefore = prototype.insertBefore;
  prototype.insertBefore = function insertBefore<T extends Node>(
    node: T,
    child: Node | null,
  ): T {
    if (child && child.parentNode !== this) {
      const parent = child.parentNode;
      if (parent) {
        return nativeInsertBefore.call(parent, node, child) as T;
      }
      return nativeInsertBefore.call(this, node, null) as T;
    }
    return nativeInsertBefore.call(this, node, child) as T;
  };
}
